import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { OperationsHistoryItem } from '@/entities/operation';
import {
  useAppSettingsStore,
  useDateLocale,
  useGroupOperationsByDate,
} from '@/shared/lib';
import type {
  OperationDto,
  OperationStatusIcon,
  OperationTypeIcon,
} from '@/shared/types';
import { SearchGroupTitle } from './SearchGroupTitle';

const HISTORY_ITEM_CLASSNAME =
  'px-0 rounded-none border-0 hover:bg-transparent cursor-default [&_.bg-secondary-bg]:bg-white/8 [&_.text-primary-cyan]:text-cyan';

type Props = {
  operations: Array<OperationDto>;
  statusIcons: Array<OperationStatusIcon>;
  typeIcons: Array<OperationTypeIcon>;
  withTitle?: boolean;
  action?: React.ReactNode;
};

const SearchHistoryGroup = ({
  operations,
  statusIcons,
  typeIcons,
  withTitle = true,
  action,
}: Props) => {
  const { t } = useTranslation();
  const { isBalancesHidden } = useAppSettingsStore();
  const { dateLocale } = useDateLocale();

  const { groupedOperations } = useGroupOperationsByDate(operations);

  if (operations.length === 0 && !action) {
    return null;
  }

  return (
    <div className="flex flex-col gap-[0.8rem]">
      {withTitle && (
        <SearchGroupTitle>
          {t('personalAccount.globalSearch.categories.history')}
        </SearchGroupTitle>
      )}

      <div className="flex flex-col gap-[2.4rem]">
        {Object.entries(groupedOperations).map(
          ([monthYear, monthOperations]) => (
            <div key={monthYear} className="flex flex-col gap-[0.8rem]">
              <span className="headline5 text-primary capitalize">
                {format(new Date(`${monthYear}-01`), 'LLLL yyyy', {
                  locale: dateLocale,
                })}
              </span>

              <div className="flex flex-col">
                {monthOperations.map(operation => (
                  <OperationsHistoryItem
                    key={operation.operaionid}
                    operation={operation}
                    operationStatusIcons={statusIcons}
                    operationTypeIcons={typeIcons}
                    isBalancesHidden={isBalancesHidden}
                    variant="compact"
                    className={HISTORY_ITEM_CLASSNAME}
                    statusClassName="border-0"
                  />
                ))}
              </div>
            </div>
          )
        )}
      </div>

      {action}
    </div>
  );
};

export { SearchHistoryGroup };
